/**
 * حساب خصم الغياب/التأخرات كما في السجل التفصيلي (mergeAttendanceData + localTotals).
 * يُستخدم في تقارير الخصومات عندما API discounts-list لا يطابق السجل بعد.
 */
import axios from "axios";
import dayjs from "dayjs";
import {
  getDisplayedDayLateSeconds as calcDisplayedDayLateSeconds,
  isFullApprovedVacationDay,
  resolveDayDiscountWithApprovedVacations,
  rowHasAnyAttendanceStamp,
  secondsToLateHms,
} from "./attendanceLateDisplay";
import {
  calcAbsenceDiscountAmount,
  calcLateDiscountAmount,
} from "./attendanceReportDiscounts";
import { computeVacationDayShiftSlice } from "./vacationShiftDuration";

function hasMeaningfulAttendanceStamp(value) {
  if (value === null || value === undefined) return false;
  return String(value).trim() !== "";
}

function hasMeaningfulLeaveStamp(value) {
  if (value === null || value === undefined) return false;
  return String(value).trim() !== "";
}

function hasAttendanceInsideShift(record) {
  if (!record) return false;
  if (
    record.attendance_in_shift === undefined ||
    record.attendance_in_shift === null
  ) {
    return true;
  }
  return (
    record.attendance_in_shift == 1 ||
    record.attendance_in_shift === "1" ||
    record.attendance_in_shift === true
  );
}

function recordHasCompleteAttendance(att) {
  if (!att) return false;
  const mainOk =
    hasMeaningfulAttendanceStamp(att.attendance_time) &&
    hasMeaningfulLeaveStamp(att.leave_time);
  const extra = att.extra_attendance;
  const segOk = (seg) =>
    seg &&
    hasMeaningfulAttendanceStamp(seg.in) &&
    hasMeaningfulLeaveStamp(seg.out);
  if (!Array.isArray(extra) || extra.length === 0) {
    return mainOk;
  }
  if (extra.every(segOk)) {
    return true;
  }
  const last = extra[extra.length - 1];
  const onlyLastIncomplete =
    extra.length >= 1 &&
    !segOk(last) &&
    extra.slice(0, -1).every(segOk) &&
    mainOk;
  return onlyLastIncomplete || false;
}

function recordQualifiesAsCompleteAttendance(att) {
  return recordHasCompleteAttendance(att) && hasAttendanceInsideShift(att);
}

function parseTimeToSeconds(timeStr) {
  if (!timeStr || timeStr === "0") return 0;
  const parts = String(timeStr).split(":");
  if (parts.length < 2) return 0;
  const h = parseInt(parts[0], 10) || 0;
  const m = parseInt(parts[1], 10) || 0;
  const s = parseInt(parts[2], 10) || 0;
  return h * 3600 + m * 60 + s;
}

function parseLateToMinutesValue(lateTime) {
  if (!lateTime) return 0;
  const parts = String(lateTime).split(":");
  const h = parseInt(parts[0], 10) || 0;
  const m = parseInt(parts[1], 10) || 0;
  return h * 60 + m;
}

function generateAllDays(startDate, endDate) {
  const days = [];
  const dayNames = [
    "الأحد",
    "الإثنين",
    "الثلاثاء",
    "الأربعاء",
    "الخميس",
    "الجمعة",
    "السبت",
  ];
  let current = dayjs(startDate);
  const end = dayjs(endDate);
  while (current.isBefore(end) || current.isSame(end, "day")) {
    days.push({
      date: current.format("YYYY-MM-DD"),
      dayName: dayNames[current.day()],
      attendance_time: null,
      leave_time: null,
      workHours: "00:00:00",
      lateTime: "00:00:00",
      discount: 0,
      extra_attendance: [],
    });
    current = current.add(1, "day");
  }
  return days;
}

function parseExcludedDatesResponse(data) {
  if (!data) return [];
  if (Array.isArray(data)) {
    return data
      .map((item) => {
        if (typeof item === "string") return item.split(" ")[0];
        return item?.date ? String(item.date).split(" ")[0] : null;
      })
      .filter(Boolean);
  }
  if (Array.isArray(data.dates)) {
    return data.dates.map((d) => String(d).split(" ")[0]);
  }
  return [];
}

function mergeAttendanceData(
  attendanceData,
  allDays,
  excludedDatesList,
  dailySalaryValue,
  weekendDaysList,
  baseSalary,
  vacationsList,
  dailyDurationsMap,
  dailyShiftRangesMap,
  durationFallback,
) {
  const fullDayGross =
    baseSalary > 0
      ? baseSalary / 30
      : dailySalaryValue > 0
        ? dailySalaryValue
        : 0;

  const attendanceMap = new Map();
  (attendanceData || []).forEach((item) => {
    const existing = attendanceMap.get(item.date);
    if (!existing || Number(item.accepted) > Number(existing.accepted)) {
      attendanceMap.set(item.date, item);
    }
  });

  return allDays.map((day) => {
    const attendance = attendanceMap.get(day.date);
    const currentDay = dayjs(day.date);

    const matchingVacations = Array.isArray(vacationsList)
      ? vacationsList.filter((v) => {
          if (!v.date_from || !v.date_to) return false;
          const vacStart = dayjs(v.date_from).startOf("day");
          const vacEnd = dayjs(v.date_to).endOf("day");
          return (
            currentDay.isSame(vacStart, "day") ||
            currentDay.isSame(vacEnd, "day") ||
            (currentDay.isAfter(vacStart) && currentDay.isBefore(vacEnd))
          );
        })
      : [];

    const approvedVacations = matchingVacations.filter(
      (v) => Number(v.accepted) === 2,
    );
    const firstVacationNote =
      matchingVacations[0]?.description || matchingVacations[0]?.notes || "";

    let totalApprovedVacSecs = 0;
    approvedVacations.forEach((v) => {
      const slice = computeVacationDayShiftSlice(
        day.date,
        v,
        dailyShiftRangesMap,
        durationFallback,
      );
      totalApprovedVacSecs += slice.secs || 0;
    });

    const workDaySecs = dailyDurationsMap[day.date] || 7 * 3600;
    const vacationCredit =
      workDaySecs > 0 && fullDayGross > 0
        ? (totalApprovedVacSecs / workDaySecs) * fullDayGross
        : 0;

    if (attendance) {
      const isOffDayBySchedule =
        attendance.is_weekend == 1 ||
        attendance.is_weekend === "1" ||
        attendance.is_weekend === true;

      const isExcluded =
        attendance.is_excluded == 1 ||
        attendance.is_excluded === "1" ||
        excludedDatesList.includes(day.date) ||
        isOffDayBySchedule;

      let finalDiscount = Number(attendance.discount) || 0;
      const hasCompleteAttendance =
        recordQualifiesAsCompleteAttendance(attendance);

      if (isExcluded) {
        finalDiscount = 0;
      } else if (!hasCompleteAttendance) {
        finalDiscount = resolveDayDiscountWithApprovedVacations({
          fullDayGross,
          backendDiscount: Number(attendance.discount) || 0,
          totalApprovedVacSecs,
          vacationCredit,
          hasAnyAttendanceStamp: rowHasAnyAttendanceStamp(attendance),
        });
      }

      const isFullApprovedVac = isFullApprovedVacationDay({
        totalApprovedVacSecs,
        workDaySecs,
        finalDiscount,
      });

      return {
        ...day,
        ...attendance,
        lateTime: attendance?.lateTime || "00:00:00",
        discount: Number(finalDiscount) || 0,
        total_approved_vac_secs: totalApprovedVacSecs,
        is_full_approved_vac: isFullApprovedVac ? 1 : 0,
        is_excluded: isExcluded ? 1 : 0,
        is_weekend: isOffDayBySchedule
          ? 1
          : attendance.is_weekend != null
            ? attendance.is_weekend
            : 0,
      };
    }

    const backendRow = (attendanceData || []).find((d) => d && d.date === day.date);
    const isOffByScheduleFromBackend = backendRow
      ? backendRow.is_weekend == 1 ||
        backendRow.is_weekend === "1" ||
        backendRow.is_weekend === true
      : false;
    const isExcluded =
      excludedDatesList.includes(day.date) || isOffByScheduleFromBackend;

    let discount = 0;
    if (isExcluded) {
      discount = 0;
    } else if (totalApprovedVacSecs > 0) {
      discount = Math.max(0, fullDayGross - vacationCredit);
    } else {
      discount =
        backendRow && backendRow.discount !== undefined
          ? Number(backendRow.discount)
          : fullDayGross;
    }

    const isFullApprovedVac = isFullApprovedVacationDay({
      totalApprovedVacSecs,
      workDaySecs,
      finalDiscount: discount,
    });

    return {
      ...day,
      discount,
      total_approved_vac_secs: totalApprovedVacSecs,
      is_full_approved_vac: isFullApprovedVac ? 1 : 0,
      is_excluded: isExcluded ? 1 : 0,
      is_weekend: isOffByScheduleFromBackend ? 1 : 0,
    };
  });
}

function computeDetailDiscountTotals(
  mergedData,
  salary,
  dsalary,
  weekendDays,
  options = {},
) {
  let lateMinutes = 0;
  let lateSeconds = 0;
  let attDays = 0;
  let absentDays = 0;
  let absentDeductions = 0;
  let lateDeductions = 0;

  const safeSalary = Number(salary) || 0;
  const { userStatus, discountsRound = 0, totalDays: totalDaysOpt = 0 } =
    options;
  const dailyRate =
    safeSalary > 0 ? safeSalary / 30 : Number(dsalary) > 0 ? Number(dsalary) : 0;

  const getDailyLateDiscount = (item, requiredDailyMinutes, dailyDurations) => {
    const isWeekend =
      item.is_weekend == 1 ||
      item.is_weekend === "1" ||
      item.is_weekend === true ||
      (weekendDays || []).includes(item.dayName);
    const isHoliday = item.is_excluded == 1 || item.is_excluded === "1";
    const workDaySecsLate =
      Number(dailyDurations?.[item.date]) > 0
        ? Number(dailyDurations[item.date])
        : (Number(requiredDailyMinutes) || 7 * 60) * 60;
    const hasFullApprovedVacation =
      isFullApprovedVacationDay({
        totalApprovedVacSecs: Number(item.total_approved_vac_secs) || 0,
        workDaySecs: workDaySecsLate,
        finalDiscount: item.discount,
      }) ||
      item.is_full_approved_vac === 1 ||
      item.is_full_approved_vac === "1" ||
      item.is_full_approved_vac === true;
    const isWorkDay = !isWeekend && !isHoliday;
    if (!isWorkDay || hasFullApprovedVacation) return 0;

    const lateSecs = parseTimeToSeconds(item.lateTime);
    if (lateSecs <= 0) return 0;
    return calcLateDiscountAmount(
      safeSalary,
      requiredDailyMinutes,
      lateSecs,
      true,
    );
  };

  const getDisplayedDayDiscount = (item, requiredDailyMinutes, dailyDurations) => {
    const isWeekend =
      item.is_weekend == 1 ||
      item.is_weekend === "1" ||
      item.is_weekend === true;
    const isHoliday = item.is_excluded == 1 || item.is_excluded === "1";
    const workDaySecsDisc =
      Number(dailyDurations?.[item.date]) > 0
        ? Number(dailyDurations[item.date])
        : (Number(requiredDailyMinutes) || 7 * 60) * 60;
    const hasFullApprovedVacation =
      isFullApprovedVacationDay({
        totalApprovedVacSecs: Number(item.total_approved_vac_secs) || 0,
        workDaySecs: workDaySecsDisc,
        finalDiscount: item.discount,
      }) ||
      item.is_full_approved_vac === 1 ||
      item.is_full_approved_vac === "1" ||
      item.is_full_approved_vac === true;
    const isWorkDay = !isWeekend && !isHoliday;
    if (!isWorkDay || hasFullApprovedVacation) return 0;
    if (!recordQualifiesAsCompleteAttendance(item)) {
      return Number(item.discount) || 0;
    }
    return getDailyLateDiscount(item, requiredDailyMinutes, dailyDurations);
  };

  let requiredDailyMinutes = 7 * 60;
  const dailyDurations = {};
  mergedData.forEach((item) => {
    const isWeekend =
      item.is_weekend == 1 ||
      item.is_weekend === "1" ||
      item.is_weekend === true;
    const isHoliday = item.is_excluded == 1 || item.is_excluded === "1";
    if (!isWeekend && !isHoliday) {
      const secs = parseTimeToSeconds(item.duartion);
      if (secs > 0) dailyDurations[item.date] = secs;
    }
  });
  const reqVals = Object.values(dailyDurations).filter((v) => v > 0);
  if (reqVals.length) {
    requiredDailyMinutes =
      reqVals.reduce((a, b) => a + b, 0) / reqVals.length / 60;
  }

  mergedData.forEach((item) => {
    const hasAnyAttendanceStamp =
      hasMeaningfulAttendanceStamp(item.attendance_time) ||
      hasMeaningfulLeaveStamp(item.leave_time) ||
      (Array.isArray(item.extra_attendance) &&
        item.extra_attendance.some(
          (seg) =>
            hasMeaningfulAttendanceStamp(seg?.in) ||
            hasMeaningfulLeaveStamp(seg?.out),
        ));

    const vacationTypeId = Number(item.type ?? item.vacationtype_id ?? 0);
    const vacationTypeName = String(item.vac_type_name || item.types || "");
    const isHrApprovedVacation =
      Number(item.accepted) === 2 || Number(item.vac_status) === 2;
    const isMissionVacation =
      isHrApprovedVacation &&
      (vacationTypeId === 1 || vacationTypeName.includes("مهمة"));
    const attendedForActualCount =
      (hasAnyAttendanceStamp && hasAttendanceInsideShift(item)) ||
      isMissionVacation;
    if (attendedForActualCount) attDays++;

    const isWeekend =
      item.is_weekend == 1 ||
      item.is_weekend === "1" ||
      item.is_weekend === true;
    const isHoliday = item.is_excluded == 1 || item.is_excluded === "1";
    const isWorkDay = !isWeekend && !isHoliday;
    const dayWorkSecs =
      Number(dailyDurations[item.date]) > 0
        ? Number(dailyDurations[item.date])
        : (Number(requiredDailyMinutes) || 7 * 60) * 60;
    const approvedVacSecs = Number(item.total_approved_vac_secs) || 0;
    const hasFullApprovedVacation =
      isFullApprovedVacationDay({
        totalApprovedVacSecs: approvedVacSecs,
        workDaySecs: dayWorkSecs,
        finalDiscount: item.discount,
      }) ||
      item.is_full_approved_vac === 1 ||
      item.is_full_approved_vac === "1" ||
      item.is_full_approved_vac === true;

    if (isWorkDay && !hasAnyAttendanceStamp && !hasFullApprovedVacation) {
      absentDays++;
    }

    if (isWorkDay && !hasFullApprovedVacation && hasAnyAttendanceStamp) {
      const dayLateSecs = calcDisplayedDayLateSeconds(item, {
        recordQualifiesAsCompleteAttendance,
        getDisplayedDayDiscount: (row) =>
          getDisplayedDayDiscount(row, requiredDailyMinutes, dailyDurations),
        dailyDurations,
        requiredDailyMinutes,
        salary: safeSalary,
      });
      if (dayLateSecs > 0) {
        lateSeconds += dayLateSecs;
      }
    }
  });

  absentDeductions = calcAbsenceDiscountAmount(
    safeSalary,
    dsalary,
    absentDays,
  );
  lateDeductions = calcLateDiscountAmount(
    safeSalary,
    requiredDailyMinutes,
    lateSeconds,
    true,
  );
  lateMinutes = Math.round(lateSeconds / 60);

  let totalDeductions = Number(lateDeductions) + Number(absentDeductions);
  if (discountsRound > 0) {
    totalDeductions = Math.round(totalDeductions / discountsRound) * discountsRound;
  } else {
    totalDeductions = Math.round(totalDeductions);
  }

  const itemsCount = mergedData?.length || totalDaysOpt || 30;
  const totalPotentialSalary =
    userStatus == 16 ? safeSalary : safeSalary * itemsCount;
  const disciplinePercentage =
    totalPotentialSalary > 0
      ? Math.max(0, Math.min(1, 1 - totalDeductions / totalPotentialSalary))
      : 0;

  return {
    absenceDays: absentDays,
    attendanceDays: attDays,
    absenceDiscount: Math.round(absentDeductions),
    lateDiscount: Math.round(lateDeductions),
    totalDiscount: Math.round(absentDeductions + lateDeductions),
    lateTimeFormatted: secondsToLateHms(lateSeconds),
    lateMinutes,
    disciplinePercentage,
  };
}

function getDurationFallback(setting) {
  const find = (key) => setting?.find((i) => i.key === key)?.value;
  return {
    start: find("duration_start") || "07:00:00",
    end: find("duration_end") || "14:00:00",
  };
}

function getWeekendDays(setting) {
  try {
    const raw = setting?.find((i) => i.key === "admin.weekend_days")?.value;
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

/**
 * حساب خصومات موظف واحد من attendancelog + dawam-info (نفس السجل التفصيلي).
 */
export async function fetchDetailDiscountTotalsForUser(
  userId,
  start,
  end,
  baseUrl,
  setting,
  salaryFallback = 0,
  userStatus,
) {
  const [logRes, infoRes, exclRes] = await Promise.all([
    axios.get(`${baseUrl}attendancelog/${userId}/${start}/${end}`),
    axios.get(`${baseUrl}dawam-info/${userId}/${start}/${end}`),
    axios
      .get(`${baseUrl}excluded-dates/${userId}/${start}/${end}`)
      .catch(() => ({ data: [] })),
  ]);

  const salary =
    Number(infoRes.data?.data?.[0]?.salary) ||
    Number(salaryFallback) ||
    0;
  const dsalary =
    Number(infoRes.data?.data?.[0]?.dsalary) ||
    (salary > 0 ? salary / 30 : 0);

  const vacations = infoRes.data?.vacs_list || infoRes.data?.tasksAmount || [];
  const dailyDurations = infoRes.data?.daily_durations || {};
  const dailyShiftRanges = infoRes.data?.daily_shift_ranges || {};
  const excludedDates = parseExcludedDatesResponse(exclRes.data);
  const weekendDays = getWeekendDays(setting);
  const durationFallback = getDurationFallback(setting);

  const allDays = generateAllDays(start, end);
  const merged = mergeAttendanceData(
    logRes.data || [],
    allDays,
    excludedDates,
    dsalary,
    weekendDays,
    salary,
    vacations,
    dailyDurations,
    dailyShiftRanges,
    durationFallback,
  );

  const totalDays = Number(infoRes.data?.count?.[0]?.count || 0);
  const discountsRound = Number(
    setting?.find((i) => i.key === "admin.discounts_round")?.value || 0,
  );

  const totals = computeDetailDiscountTotals(
    merged,
    salary,
    dsalary,
    weekendDays,
    { userStatus, discountsRound, totalDays },
  );

  return {
    ...totals,
    requiredWorkDays: totalDays,
  };
}

const ENRICH_CONCURRENCY = 6;

/**
 * يحدّث صفوف discounts-list / wages-list من منطق السجل التفصيلي.
 */
export async function enrichDiscountListFromDetailRecord(
  lists,
  start,
  end,
  setting,
  baseUrl,
) {
  if (!Array.isArray(lists) || !lists.length) return lists;

  const targets = lists.filter((row) => row.fingerprint_type == "22");
  for (let i = 0; i < targets.length; i += ENRICH_CONCURRENCY) {
    const batch = targets.slice(i, i + ENRICH_CONCURRENCY);
    await Promise.all(
      batch.map(async (row) => {
        try {
          const totals = await fetchDetailDiscountTotalsForUser(
            row.user_id,
            start,
            end,
            baseUrl,
            setting,
            row.salary,
          );
          row.lateTimePrice = totals.lateDiscount;
          row.absenceDiscount = totals.absenceDiscount;
          row.attendanceDiscountTotal = totals.totalDiscount;
          row.absenceDays = totals.absenceDays;
          row.lateTime = totals.lateTimeFormatted;
          row._fromDetailRecord = true;
        } catch (err) {
          console.warn("detail discount enrich failed", row.user_id, err);
        }
      }),
    );
  }
  return lists;
}
